import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { describeError } from '../../lib/describeError'
import { pieceVersions, type PieceDetail, type SourcePhoto } from '../api'
import { destinationLabel } from '../labels'
import { archiveArticleHref, pieceHref } from '../links'
import { useEditorialMe } from '../me'
import type { FigureAttrs } from './figure'
import { choosePhoto } from './photoApi'
import { SAVE_FIRST } from './pieceHelpers'
import { articleSourceDisplay, photoFigure, sourceParagraphs } from './sourcePhotos'
import { btnSmall, errorText, focus, link } from './ui'

const LABEL = 'text-[12px] font-bold uppercase tracking-[0.08em] text-paper-faint'

type Props = {
  detail: PieceDetail
  path: string
  sectionLabel: string
  /** The piece is open for edits (not live, not a writer's locked piece, no run on it). */
  editable: boolean
  beforeWrite: () => Promise<boolean>
  onChanged: () => void
  onInsertFigure: (a: FigureAttrs) => void
}

export function VersionsTab({ detail, path, sectionLabel, editable, beforeWrite, onChanged, onInsertFigure }: Props) {
  const q = useQuery({ queryKey: ['piece-versions', detail.id], queryFn: () => pieceVersions(detail.id) })
  const me = useEditorialMe()
  const [note, setNote] = useState<string | null>(null)
  const lead = useMutation({
    mutationFn: async (assetId: number) => {
      if (!(await beforeWrite())) throw new Error(SAVE_FIRST)
      return choosePhoto(detail.id, assetId)
    },
    onSuccess: () => { setNote('Lead photo changed.'); onChanged() },
  })
  if (q.isLoading) return <p className="m-0 text-[13px] text-paper-faint">Loading…</p>
  if (q.isError || !q.data) return <p className="m-0 text-[13px] text-radar-red">Couldn't load the versions.</p>
  const { source, versions, source_photos: photos, source_text: text } = q.data
  const paragraphs = sourceParagraphs(text)
  const blank = !(detail.body_html ?? '').replace(/<[^>]*>/g, '').trim()
  return (
    <div className="flex flex-col gap-4">
      {source && (
        <div>
          <div className={LABEL}>Made from</div>
          {source.kind === 'draft'
            ? <Link className={`${link} ${focus}`} to={pieceHref(path, source.id)}>{source.title}</Link>
            : (() => {
              const a = articleSourceDisplay(me.data?.is_admin === true, source.title)
              return a.link ? <Link className={`${link} ${focus}`} to={archiveArticleHref(source.id)}>{a.text}</Link> : <span>{a.text}</span>
            })()}
        </div>
      )}
      <div>
        <div className={LABEL}>Versions</div>
        {versions.length === 0 ? <p className="m-0 text-paper-faint">No versions yet.</p> : (
          <ul className="m-0 list-none p-0">
            {versions.map((v) => (
              <li key={v.id} className="border-b border-ink-500 py-2">
                {v.id === detail.id ? <strong>{v.title} (this piece)</strong>
                  : <Link className={`${link} ${focus}`} to={pieceHref(path, v.id)}>{v.title}</Link>}
                <div className="text-[12px] text-paper-faint">
                  {v.format} · {destinationLabel(v.destination, sectionLabel)} · {v.status}{v.source_changed ? ' · source changed' : ''}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      {source && photos.length > 0 && (
        <div>
          <div className={LABEL}>Source photos</div>
          <ul className="m-0 mt-2 grid list-none grid-cols-2 gap-3 p-0">
            {photos.map((p: SourcePhoto) => (
              <li key={p.id} className="flex flex-col gap-1.5">
                <img src={p.url} alt={p.caption ?? ''} loading="lazy" className="aspect-[4/3] w-full rounded-md object-cover" />
                {p.caption && <span className="text-[12px] text-paper-faint">{p.caption}</span>}
                <button type="button" className={btnSmall} disabled={!editable || lead.isPending} onClick={() => { setNote(null); lead.mutate(p.id) }}>
                  Use as lead photo
                </button>
                <button type="button" className={btnSmall} disabled={!editable} onClick={() => onInsertFigure(photoFigure(p))}>
                  Add to the body
                </button>
              </li>
            ))}
          </ul>
          {!editable && <p className="m-0 mt-2 text-[12px] text-paper-faint">These can't change on this piece.</p>}
          {lead.isError && <p role="alert" className={errorText}>{describeError(lead.error)}</p>}
          {note && <p role="status" className="m-0 mt-2 text-[12px] text-paper-faint">{note}</p>}
        </div>
      )}
      {source && paragraphs.length > 0 && (
        <details open={blank}>
          <summary className={`cursor-pointer font-semibold ${focus}`}>Show the source text</summary>
          <div className="mt-2 max-h-[60vh] overflow-y-auto rounded-lg border border-ink-500 p-3 font-serif text-[15px] leading-relaxed">
            {paragraphs.map((p, i) => <p key={i} className="m-0 mb-3 last:mb-0">{p}</p>)}
          </div>
        </details>
      )}
    </div>
  )
}
